import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { Check, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  useNativeBridge,
  triggerNativePurchase,
  triggerNativeRestore,
  registerIapResultHandler,
} from "../lib/nativeBridge";
import { toast } from "sonner";

const PLANS = [
  {
    tier: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    features: [
      "Plant identification (AI vision)",
      "Disease detection — name + symptoms only",
      "General Care library (browse all conditions)",
      "30-second previews of every music track",
      "Basic care guide on each scan",
      "Ad-supported",
    ],
    cta: "Current Plan",
    accent: "neutral",
  },
  {
    tier: "premium",
    name: "Premium",
    monthly: 4.99,
    annual: 39.99,
    features: [
      "Everything in Free",
      "Full 5-step disease remedy plans (organic + chemical + preventive)",
      "Country & weather-adapted climate care tips",
      "Live weather card on every scan",
      "Full access to all Premium music tracks",
      "30-second previews of Platinum-tier tracks",
      "Ad-free experience · 50 scans per day",
    ],
    cta: "Upgrade",
    accent: "green",
    popular: true,
  },
  {
    tier: "platinum",
    name: "Platinum",
    monthly: 9.99,
    annual: 79.99,
    features: [
      "Everything in Premium",
      "Platinum Deep-Dive on plant scans — companion plants, pet & human safety, year-round care timeline, botanical reference",
      "Platinum Deep-Dive on disease scans — full recovery plan with treatment mix + recommended sequence",
      "Full access to ALL music (Premium + Platinum tracks)",
      "Create unlimited personal playlists",
      "Plant Growth Journal with weekly AI reports",
      "Download journals as ZIP (branded PDF + photos)",
      "Unlimited scans · priority support",
    ],
    cta: "Go Platinum",
    accent: "gold",
  },
];

export default function SubscriptionScreen() {
  const [billing, setBilling] = useState("monthly");
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [busyTier, setBusyTier] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const bridge = useNativeBridge();
  const nativeIOS = bridge.ios;
  const nativeAndroid = bridge.android;
  const isNativeMobile = nativeIOS || nativeAndroid;
  const busyTierRef = useRef(null);
  useEffect(() => { busyTierRef.current = busyTier; }, [busyTier]);

  // Wire the callback the Swift/Android side calls after a purchase.
  useEffect(() => {
    const off = registerIapResultHandler(async (payload) => {
      const status = payload?.status;
      if (status === "success") {
        toast.success("Purchase successful — updating your account…");
        try { await refresh(); } catch (_e) { /* ignore refresh error */ }
        setBusyTier(null);
        navigate("/home");
      } else if (status === "cancelled") {
        toast.info("Purchase cancelled");
        setBusyTier(null);
      } else if (status === "restored") {
        // Swift returned a list of active StoreKit entitlements. Verify each
        // with the server so we activate the correct tier for this user.
        const txs = Array.isArray(payload.transactions) ? payload.transactions : [];
        if (txs.length === 0) {
          toast.info("No previous purchases found on this Apple ID");
          setRestoring(false);
          return;
        }
        let anySuccess = false;
        let lastError = null;
        for (const t of txs) {
          if (!t?.transactionId || !t?.productId) continue;
          try {
            await api.post("/iap/verify-ios", {
              transactionId: String(t.transactionId),
              productId: String(t.productId),
            });
            anySuccess = true;
          } catch (e) {
            lastError = e?.response?.data?.detail || e?.message || "Verification failed";
          }
        }
        if (anySuccess) {
          toast.success("Purchases restored");
          try { await refresh(); } catch (_e) { /* ignore refresh error */ }
          setRestoring(false);
          navigate("/home");
        } else {
          toast.error(lastError || "Could not restore purchases");
          setRestoring(false);
        }
      } else if (status === "nothing-to-restore") {
        toast.info("No previous purchases found on this Apple ID");
        setRestoring(false);
      } else {
        toast.error(payload?.error || "Purchase failed. Please try again.");
        setBusyTier(null);
        setRestoring(false);
      }
    });
    return off;
  }, [navigate, refresh]);

  // Safety net: if a native purchase never resolves (Swift side didn't call
  // back), clear "Processing…" after 90s so the button is not permanently stuck.
  useEffect(() => {
    if (!busyTier || !isNativeMobile) return;
    const t = setTimeout(() => {
      if (busyTierRef.current) {
        toast.error("Purchase timed out. Please try again.");
        setBusyTier(null);
      }
    }, 90_000);
    return () => clearTimeout(t);
  }, [busyTier, isNativeMobile]);

  // Safety net for restore — Swift should respond quickly (no user UI).
  useEffect(() => {
    if (!restoring) return;
    const t = setTimeout(() => {
      setRestoring((cur) => {
        if (cur) toast.error("Restore timed out. Please try again.");
        return false;
      });
    }, 30_000);
    return () => clearTimeout(t);
  }, [restoring]);

  const restore = () => {
    if (!isNativeMobile) return;
    setRestoring(true);
    const ok = triggerNativeRestore();
    if (ok) {
      toast.info("Checking for previous purchases…");
    } else {
      toast.error("Restore is unavailable. Please update the app.");
      setRestoring(false);
    }
  };

  const upgrade = async (tier) => {
    // Free tier stays server-side (downgrade / cancel) regardless of platform.
    if (tier === "free") {
      setBusyTier(tier);
      try {
        await api.post("/subscriptions/upgrade", { tier, billing, origin_url: window.location.origin });
        toast.success("Subscription cancelled");
        await refresh();
        navigate("/home");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Update failed");
      } finally {
        setBusyTier(null);
      }
      return;
    }

    // Inside native iOS/Android wrapper → route through Apple/Google IAP
    // instead of Stripe web checkout. Apple's App Store rules require this.
    if (isNativeMobile) {
      setBusyTier(tier);
      const ok = triggerNativePurchase({ tier, billing });
      if (ok) {
        toast.info("Opening secure payment…");
        // Native side will call window.__CFF_ON_IAP_RESULT__ when done.
      } else {
        toast.error("In-app purchase is unavailable. Please update the app or try again.");
        setBusyTier(null);
      }
      return;
    }

    // Web browser path — hosted Stripe Checkout. Explicitly block this if
    // the UA looks like an iOS webview but the bridge wasn't injected —
    // Stripe hosted checkout does not work reliably inside WKWebView and
    // Apple review policy forbids paying for digital goods with Stripe on
    // iOS anyway.
    if (bridge.iosLikelyBroken) {
      toast.error(
        "In-app purchase is not available in this app version. Please reload the app or update to the latest version."
      );
      return;
    }

    setBusyTier(tier);
    try {
      const { data } = await api.post("/subscriptions/upgrade", {
        tier,
        billing,
        origin_url: window.location.origin,
      });
      if (data.checkout && data.url) {
        window.location.href = data.url;
        return;
      }
      toast.success("Plan updated");
      await refresh();
      navigate("/home");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed");
      setBusyTier(null);
    }
  };

  return (
    <Shell section="subscription">
      <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-shadow-green">Choose your plan</h1>
      <p className="text-soft text-sm mb-2">Cancel anytime. 7-day free trial on paid tiers.</p>
      {isNativeMobile && (
        <p className="text-[11px] mb-5" style={{ color: "#A5D6A7" }} data-testid="sub-native-notice">
          Billed securely through {nativeIOS ? "Apple" : "Google"}. Manage or cancel in your device settings.
        </p>
      )}
      {!isNativeMobile && bridge.iosLikelyBroken && (
        <div
          className="mb-5 p-3 rounded-lg flex items-start gap-2"
          style={{
            background: "rgba(255, 152, 0, 0.12)",
            border: "1px solid rgba(255, 152, 0, 0.45)",
          }}
          data-testid="sub-native-warning"
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" style={{ color: "#FFB74D" }} />
          <p className="text-[12px] leading-relaxed" style={{ color: "#FFE0B2" }}>
            In-app purchase bridge not detected. Tap the <b>Reload</b> banner (if shown) or fully close and reopen the app to update to the latest version. If this keeps happening, contact support.
          </p>
        </div>
      )}
      {!isNativeMobile && !bridge.iosLikelyBroken && <div className="mb-5" />}

      <div className="inline-flex items-center bg-black/40 backdrop-blur-md rounded-full p-1 mb-6" data-testid="billing-toggle">
        {["monthly", "annual"].map((b) => (
          <button
            key={b}
            onClick={() => setBilling(b)}
            className={`px-4 py-2 rounded-full text-xs font-semibold capitalize transition-all ${
              billing === b ? "bg-[#4CAF50] text-white" : "text-white/60"
            }`}
            style={billing === b ? { boxShadow: "var(--glow-green)" } : {}}
            data-testid={`billing-${b}`}
          >
            {b} {b === "annual" && <span className="ml-1 text-[10px] opacity-80">Save 33%</span>}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {PLANS.map((p, idx) => {
          const price = billing === "monthly" ? p.monthly : p.annual;
          const isCurrent = user?.tier === p.tier;
          const isUpgrade = !isCurrent && p.tier !== "free";
          return (
            <div
              key={p.tier}
              className={`glass-card-strong p-6 fade-up stagger-${idx + 1} relative`}
              style={
                p.accent === "gold"
                  ? { borderColor: "rgba(255,213,79,0.5)", boxShadow: "0 0 24px rgba(255,213,79,0.25)" }
                  : p.accent === "green"
                    ? { borderColor: "rgba(76,175,80,0.5)", boxShadow: "var(--glow-green)" }
                    : {}
              }
              data-testid={`plan-${p.tier}`}
            >
              {p.popular && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full"
                  style={{
                    background: "rgba(76,175,80,0.95)",
                    boxShadow: "0 0 18px rgba(76,175,80,0.7)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: "#0a0a0a",
                  }}
                  data-testid={`popular-ribbon-${p.tier}`}
                >
                  <Sparkles size={11} /> MOST POPULAR
                </span>
              )}
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-xl font-bold">{p.name}</h3>
                <div className="text-right">
                  <span
                    className="text-2xl font-extrabold"
                    style={{ color: p.tier === "free" ? "rgba(255,255,255,0.7)" : "#4CAF50", textShadow: p.tier !== "free" ? "0 0 14px rgba(76,175,80,0.5)" : "none" }}
                  >
                    {price === 0 ? "Free" : `$${price.toFixed(2)}`}
                  </span>
                  {price > 0 && <span className="text-xs text-muted-soft">/{billing === "monthly" ? "mo" : "yr"}</span>}
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-soft">
                    <Check size={14} className="text-[#4CAF50] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled className="w-full btn-outline opacity-60 cursor-not-allowed">Current Plan</button>
              ) : p.tier === "free" ? (
                <button onClick={() => upgrade("free")} className="w-full btn-outline" disabled={busyTier === "free"}>Downgrade</button>
              ) : (
                <button
                  onClick={() => upgrade(p.tier)}
                  disabled={!!busyTier}
                  className={`w-full ${p.accent === "gold" ? "btn-gold" : "btn-primary"}`}
                  data-testid={`upgrade-button-${p.tier}`}
                >
                  {busyTier === p.tier ? "Processing…" : p.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-center text-[11px] text-muted-soft mt-6">
        {isNativeMobile
          ? `Payments processed by ${nativeIOS ? "Apple" : "Google"}. Manage or cancel anytime in your device Subscriptions settings.`
          : "Secure payments by Stripe. Cancel anytime."}
      </p>

      {isNativeMobile && (
        <div className="text-center mt-3">
          <button
            type="button"
            onClick={restore}
            disabled={restoring || !!busyTier}
            className="text-[12px] underline underline-offset-4 disabled:opacity-40 transition-opacity"
            style={{ color: "#A5D6A7" }}
            data-testid="restore-purchases-button"
          >
            {restoring ? "Restoring…" : "Restore purchases"}
          </button>
        </div>
      )}
    </Shell>
  );
}
